import { Howl } from 'howler'
import { getSceneImagePath, assetUrl, applyResponsiveImage } from '../../../utils/pathResolver'
import { SaveStateManager } from '../../../features/save-state'
import { getAudioManager } from '../../audio'
import type { AnimationContext } from './types'

const SFX_BASE = '/assets/audio/sfx'
const VISIBILITY_THRESHOLD = 0.2
const FADE_DURATION = 400

export function setupClickReveal(context: AnimationContext): void {
  if (!context.imageContainer || !context.data.clickReveal) return

  const { image, area, audio } = context.data.clickReveal
  const sceneId = context.data.id

  const setup = () => {
    const img = context.imageContainer?.querySelector('img')
    if (!img || !img.complete) {
      setTimeout(setup, 100)
      return
    }

    const saved = SaveStateManager.load()
    let isRevealed = saved?.clickReveals?.includes(sceneId) ?? false

    const btn = document.createElement('button')
    btn.className = 'click-reveal__button'
    btn.setAttribute('aria-label', 'Odhal poklad')
    btn.style.left = `${area.x}%`
    btn.style.top = `${area.y}%`
    btn.style.width = `${area.width}%`
    btn.style.height = `${area.height}%`

    const revealImg = document.createElement('img')
    revealImg.src = getSceneImagePath(sceneId, image)
    revealImg.alt = ''
    revealImg.className = 'click-reveal__image'
    revealImg.draggable = false
    applyResponsiveImage(revealImg)
    btn.appendChild(revealImg)

    if (isRevealed) {
      revealImg.classList.add('click-reveal__image--visible')
      btn.disabled = true
    }

    // --- Audio ---
    let howl: Howl | null = null
    let soundId: number | null = null
    let isAudible = false

    if (audio) {
      howl = new Howl({
        src: [assetUrl(`${SFX_BASE}/${audio}`)],
        loop: true,
        volume: 0,
        preload: true,
        html5: false,
      })
    }

    const getGlobalSfxVolume = (): number => getAudioManager().getVolume()

    const getVisibility = (): number => {
      const rect = context.container.getBoundingClientRect()
      const vh = window.innerHeight
      const visibleTop = Math.max(0, rect.top)
      const visibleBottom = Math.min(vh, rect.bottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      return rect.height > 0 ? visibleHeight / rect.height : 0
    }

    const computeVolume = (visibility: number): number => {
      if (visibility < VISIBILITY_THRESHOLD) return 0
      const t = (visibility - VISIBILITY_THRESHOLD) / (1 - VISIBILITY_THRESHOLD)
      if (isRevealed) {
        return t
      }
      return 0.1 + t * 0.4
    }

    const updateAudio = () => {
      if (!howl) return

      const visibility = getVisibility()
      const fraction = computeVolume(visibility)
      const effectiveVolume = fraction * getGlobalSfxVolume()

      if (fraction > 0 && !isAudible) {
        if (soundId === null) {
          soundId = howl.play() as unknown as number
          howl.volume(effectiveVolume, soundId)
        } else {
          howl.volume(0, soundId)
          howl.play(soundId)
          howl.fade(0, effectiveVolume, FADE_DURATION, soundId)
        }
        isAudible = true
      } else if (fraction <= 0 && isAudible) {
        if (soundId !== null) {
          howl.fade(howl.volume(soundId) as number, 0, FADE_DURATION, soundId)
          const sid = soundId
          setTimeout(() => {
            if (soundId === sid) {
              try { howl!.pause(sid) } catch { /* ignore */ }
            }
          }, FADE_DURATION)
        }
        isAudible = false
      } else if (isAudible && soundId !== null) {
        howl.volume(effectiveVolume, soundId)
      }
    }

    if (howl) {
      window.addEventListener('scroll', updateAudio, { passive: true })
      requestAnimationFrame(updateAudio)
    }

    // --- Click handler ---
    if (!isRevealed) {
      btn.addEventListener('click', () => {
        revealImg.classList.add('click-reveal__image--visible')
        btn.disabled = true
        isRevealed = true

        const state = SaveStateManager.load() ?? SaveStateManager.createInitialState()
        const reveals = state.clickReveals ?? []
        if (!reveals.includes(sceneId)) {
          reveals.push(sceneId)
        }
        state.clickReveals = reveals
        SaveStateManager.saveImmediate(state)

        if (howl && soundId !== null) {
          const targetVolume = 1.0 * getGlobalSfxVolume()
          howl.fade(howl.volume(soundId) as number, targetVolume, FADE_DURATION, soundId)
        }
      }, { once: true })
    }

    context.imageContainer!.appendChild(btn)
  }

  requestAnimationFrame(() => setTimeout(setup, 200))
}
